import { useState, useEffect, useRef } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { BookOpen, Users, ClipboardList, CreditCard, Settings, BarChart3, LogOut, Bell } from "lucide-react";
import { db } from "./lib/firebase";
import { collection, doc, onSnapshot, query, orderBy, limit, writeBatch } from "firebase/firestore";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Forum from "./screens/Forum";
import PrivacyPolicy from "./screens/PrivacyPolicy";
import RefundPolicy from "./screens/RefundPolicy";
import TermsOfUse from "./screens/TermsOfUse";
import { PayByRegistrationId } from "./screens/PayByRegistrationId";
import { PagosByRegistrationId } from "./screens/PagosByRegistrationId";
import { AzulPaymentResult } from "./screens/AzulPaymentResult";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { FinanzasScreen } from "./screens/admin/FinanzasScreen";
import { ParentLogin } from "./screens/ParentLogin";
import { ParentDashboard } from "./screens/ParentDashboard";
import { ForgotPassword } from "./screens/ForgotPassword";
import {
  AdminClassesView,
  AdminStudentsView,
  AdminRegistrationsView,
  AdminPaymentsView,
  AdminSettingsView,
} from "./components/AdminForms";

// =============================================================================
// LOGIN (Apple-style overlay)
// =============================================================================

function Login() {
  const navigate = useNavigate();
  const { signIn, error, clearError, loading } = useAuth();
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isLoggingIn = loading || submitting;

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail.trim() || !loginPassword) return;
    setSubmitting(true);
    try {
      await signIn(loginEmail.trim(), loginPassword);
      navigate("/admin", { replace: true });
    } catch {
      // error state set by AuthContext
    } finally {
      setSubmitting(false);
    }
  };

  const closeOverlay = () => navigate("/");

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-white/60 backdrop-blur-xl transition-all"
        onClick={closeOverlay}
        aria-hidden
      />
      <div className="relative w-full max-w-[440px] bg-white rounded-[32px] shadow-[0_32px_64px_rgba(0,0,0,0.12)] border border-gray-100 overflow-hidden animate-appleSlideUp">
        <div className="p-10 md:p-12">
          <div className="mb-10 text-center">
            <h2 className="text-2xl font-bold tracking-tight text-[#1d1d1f] mb-1">
              American School - Talent
            </h2>
            <p className="text-[#86868b] text-sm">Sign in to continue</p>
          </div>

          <form onSubmit={handleLoginSubmit} className="space-y-6">
            <div className="space-y-1">
              <label className="text-[12px] font-semibold text-[#86868b] ml-1">Email</label>
              <input
                type="email"
                required
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                onFocus={clearError}
                className="w-full px-4 py-3.5 bg-gray-50/50 border border-gray-200 rounded-xl outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500/50 transition-all font-medium text-[15px] text-[#1d1d1f]"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[12px] font-semibold text-[#86868b] ml-1">Password</label>
              <input
                type="password"
                required
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                onFocus={clearError}
                className="w-full px-4 py-3.5 bg-gray-50/50 border border-gray-200 rounded-xl outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500/50 transition-all font-medium text-[15px] text-[#1d1d1f]"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isLoggingIn}
              className={`w-full py-3.5 ${isLoggingIn ? "bg-blue-400" : "bg-blue-600 hover:bg-blue-700"} text-white rounded-xl font-semibold text-[15px] transition-all transform active:scale-95 shadow-lg shadow-blue-500/20`}
            >
              {isLoggingIn ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Signing in...
                </span>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

        </div>

        <button
          type="button"
          onClick={closeOverlay}
          className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-900 transition-colors"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <style>{`
        @keyframes appleSlideUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-appleSlideUp {
          animation: appleSlideUp 0.4s cubic-bezier(0.32, 0.72, 0, 1) forwards;
        }
      `}</style>
    </div>
  );
}

type AdminSection = "classes" | "students" | "registrations" | "payments" | "settings" | "finance";

const ADMIN_NAV: { id: AdminSection; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "classes", label: "Clases", icon: BookOpen },
  { id: "students", label: "Estudiantes", icon: Users },
  { id: "registrations", label: "Inscripciones", icon: ClipboardList },
  { id: "payments", label: "Pagos", icon: CreditCard },
  { id: "finance", label: "Finanzas", icon: BarChart3 },
  { id: "settings", label: "Configuración", icon: Settings },
];

const tabTransition = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -10 }, transition: { duration: 0.2 } };

function AdminDashboard() {
  const { signOut } = useAuth();
  const [activeSection, setActiveSection] = useState<AdminSection>("classes");
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [notifications, setNotifications] = useState<Array<{ id: string; message: string; createdAt?: Date | null }>>(
    []
  );
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  useEffect(() => {
    if (!db) return;
    const q = query(
      collection(db, "notifications"),
      orderBy("createdAt", "desc"),
      limit(15)
    );
    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => {
        const data = d.data() as any;
        const ts = data.createdAt;
        let createdAt: Date | null = null;
        if (ts?.toDate) createdAt = ts.toDate();
        return {
          id: d.id,
          message: data.message ?? "",
          createdAt,
        };
      });
      setNotifications(items);
      const unreadCount = snap.docs.filter((d) => !(d.data() as any).read).length;
      setNotificationCount(unreadCount);
    });
    return () => unsub();
  }, []);

  const handleMarkAllNotificationsRead = async () => {
    if (!db || notifications.length === 0) return;
    try {
      const batch = writeBatch(db as any);
      notifications.forEach((n) => {
        batch.update(doc(db as any, "notifications", n.id), { read: true });
      });
      await batch.commit();
      setNotificationsOpen(false);
    } catch (e) {
      // swallow errors; bell is best-effort
      console.error("Failed to mark notifications read", e);
    }
  };

  return (
    <div className="min-h-screen bg-[#fbfbfd] p-4 font-sans text-[#1d1d1f]">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Top Navigation Card - compact */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-sm border border-black/[0.02] p-3"
        >
          <div className="flex items-center justify-between gap-3 mb-4 px-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden bg-[#161b3d] shrink-0">
              <img
                src="/logo/logo.png"
                alt="American School"
                className="w-full h-full object-contain"
              />
              </div>
              <div>
                <h1 className="text-lg font-bold tracking-tight text-[#161b3d]">Panel de administración</h1>
              </div>
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setNotificationsOpen((o) => !o)}
                className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-gray-50 hover:bg-gray-100 border border-gray-200"
              >
                <Bell className="w-4 h-4 text-gray-600" />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-0.5 bg-red-500 text-white text-[10px] font-semibold rounded-full px-1.5 leading-none">
                    {notificationCount}
                  </span>
                )}
              </button>
              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-lg border border-gray-100 z-20">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-[13px] font-semibold text-[#111827]">Notificaciones</span>
                    {notifications.length > 0 && (
                      <button
                        type="button"
                        onClick={handleMarkAllNotificationsRead}
                        className="text-[11px] font-semibold text-[#5856D6] hover:underline"
                      >
                        Marcar como leídas
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-6 text-center text-[12px] text-gray-400">
                        No hay notificaciones recientes.
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-100">
                        {notifications.map((n) => (
                          <li key={n.id} className="px-4 py-3 text-left">
                            <p className="text-[12px] text-[#111827]">{n.message}</p>
                            {n.createdAt && (
                              <p className="mt-1 text-[11px] text-gray-400">
                                {n.createdAt.toLocaleDateString("es-DO", {
                                  day: "2-digit",
                                  month: "short",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {ADMIN_NAV.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveSection(id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                  activeSection === id
                    ? "bg-[#5856d6] text-white shadow-md shadow-indigo-200"
                    : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                }`}
              >
                <Icon className="w-3.5 h-3.5 shrink-0" />
                {label}
              </button>
            ))}
            <button
              onClick={() => {
                if (window.confirm("¿Estás seguro de que deseas cerrar sesión?")) {
                  signOut();
                }
              }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-gray-50 text-red-500 hover:bg-gray-100 whitespace-nowrap"
            >
              <LogOut className="w-3.5 h-3.5 shrink-0" />
              Cerrar sesión
            </button>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {activeSection === "classes" && (
            <motion.div key="classes" {...tabTransition}>
              <AdminClassesView />
            </motion.div>
          )}
          {activeSection === "students" && (
            <motion.div key="students" {...tabTransition}>
              <AdminStudentsView />
            </motion.div>
          )}
          {activeSection === "registrations" && (
            <motion.div key="registrations" {...tabTransition}>
              <AdminRegistrationsView />
            </motion.div>
          )}
          {activeSection === "payments" && (
            <motion.div key="payments" {...tabTransition}>
              <AdminPaymentsView />
            </motion.div>
          )}
          {activeSection === "finance" && (
            <motion.div key="finance" {...tabTransition}>
              <FinanzasScreen />
            </motion.div>
          )}
          {activeSection === "settings" && (
            <motion.div key="settings" {...tabTransition}>
              <AdminSettingsView />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// =============================================================================
// ADMIN FINANZAS PAGE (same header chrome, Finanzas content)
// =============================================================================

function AdminFinanzasPage() {
  const { signOut } = useAuth();
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [notifications, setNotifications] = useState<Array<{ id: string; message: string; createdAt?: Date | null }>>(
    []
  );
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  useEffect(() => {
    if (!db) return;
    const q = query(
      collection(db, "notifications"),
      orderBy("createdAt", "desc"),
      limit(15)
    );
    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => {
        const data = d.data() as any;
        const ts = data.createdAt;
        let createdAt: Date | null = null;
        if (ts?.toDate) createdAt = ts.toDate();
        return {
          id: d.id,
          message: data.message ?? "",
          createdAt,
        };
      });
      setNotifications(items);
      const unreadCount = snap.docs.filter((d) => !(d.data() as any).read).length;
      setNotificationCount(unreadCount);
    });
    return () => unsub();
  }, []);

  const handleMarkAllNotificationsRead = async () => {
    if (!db || notifications.length === 0) return;
    try {
      const batch = writeBatch(db as any);
      notifications.forEach((n) => {
        batch.update(doc(db as any, "notifications", n.id), { read: true });
      });
      await batch.commit();
      setNotificationsOpen(false);
    } catch (e) {
      console.error("Failed to mark notifications read", e);
    }
  };

  return (
    <div className="min-h-screen bg-[#fbfbfd] p-6 font-sans text-[#1d1d1f]">
      <div className="max-w-7xl mx-auto space-y-6">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl shadow-sm border border-black/[0.02] p-4">
          <div className="flex items-center justify-between gap-4 mb-6 px-2">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden bg-[#161b3d] shrink-0">
                <img
                  src="/logo/logo.png"
                  alt="American School"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-[#161b3d]">Panel de administración</h1>
              </div>
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setNotificationsOpen((o) => !o)}
                className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-gray-50 hover:bg-gray-100 border border-gray-200"
              >
                <Bell className="w-4 h-4 text-gray-600" />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-0.5 bg-red-500 text-white text-[10px] font-semibold rounded-full px-1.5 leading-none">
                    {notificationCount}
                  </span>
                )}
              </button>
              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-lg border border-gray-100 z-20">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-[13px] font-semibold text-[#111827]">Notificaciones</span>
                    {notifications.length > 0 && (
                      <button
                        type="button"
                        onClick={handleMarkAllNotificationsRead}
                        className="text-[11px] font-semibold text-[#5856D6] hover:underline"
                      >
                        Marcar como leídas
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-6 text-center text-[12px] text-gray-400">
                        No hay notificaciones recientes.
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-100">
                        {notifications.map((n) => (
                          <li key={n.id} className="px-4 py-3 text-left">
                            <p className="text-[12px] text-[#111827]">{n.message}</p>
                            {n.createdAt && (
                              <p className="mt-1 text-[11px] text-gray-400">
                                {n.createdAt.toLocaleDateString("es-DO", {
                                  day: "2-digit",
                                  month: "short",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/admin" className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold bg-gray-50 text-gray-500 hover:bg-gray-100 whitespace-nowrap">
              Volver
            </Link>
            <span className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold bg-[#5856d6] text-white shadow-md shadow-indigo-200 whitespace-nowrap">
              <BarChart3 className="w-4 h-4 shrink-0" />
              Finanzas
            </span>
            <button onClick={() => { if (window.confirm("¿Estás seguro de que deseas cerrar sesión?")) { signOut(); } }} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold bg-gray-50 text-red-500 hover:bg-gray-100 whitespace-nowrap">
              <LogOut className="w-4 h-4 shrink-0" />
              Cerrar sesión
            </button>
          </div>
        </motion.div>
        <FinanzasScreen />
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f5f5f7]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#5856D6]/30 border-t-[#5856D6] mx-auto"></div>
        <p className="mt-3 text-[13px] text-[#86868b]">Cargando…</p>
      </div>
    </div>
  );
}

// =============================================================================
// PROTECTED ROUTE WRAPPER
// =============================================================================

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading, authReady, isAdmin } = useAuth();

  if (!authReady || loading) {
    return <LoadingScreen />;
  }

  // Require an authenticated ADMIN — parents must not access admin routes.
  // This prevents a parent (created via submitRegistration) from navigating
  // directly to /admin after signing into the parent portal.
  if (!user || !isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
}

// =============================================================================
// PARENT ROUTE WRAPPER (sessionStorage-based, no Firebase Auth)
// =============================================================================

interface ParentRouteProps {
  children: React.ReactNode;
}

function ParentRoute({ children }: ParentRouteProps) {
  const hasSession = Boolean(sessionStorage.getItem("parentSession"));
  if (!hasSession) {
    return <Navigate to="/parent/login" replace />;
  }
  return <>{children}</>;
}

// =============================================================================
// PUBLIC ROUTE WRAPPER (redirects logged-in admin users)
// =============================================================================

interface PublicRouteProps {
  children: React.ReactNode;
}

function PublicRoute({ children }: PublicRouteProps) {
  const { user, loading, authReady, isAdmin } = useAuth();

  if (!authReady || loading) {
    return <LoadingScreen />;
  }

  // Only redirect admins; parents arriving at /admin/login should stay on the public form
  if (user && isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
}

// =============================================================================
// SIGN OUT WHEN LEAVING ADMIN (avoids sign-out on React Strict Mode unmount)
// =============================================================================

function SignOutOnLeaveAdmin() {
  const { signOut } = useAuth();
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    const wasOnAdmin = prevPathRef.current.startsWith("/admin");
    const isOnAdmin = location.pathname.startsWith("/admin");
    if (wasOnAdmin && !isOnAdmin) {
      signOut();
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname, signOut]);

  return null;
}

// =============================================================================
// APP ROUTES
// =============================================================================

function AppRoutes() {
  return (
    <>
      <SignOutOnLeaveAdmin />
      <Routes>
      {/* Admin login (public, redirects to /admin when already logged in) */}
      <Route
        path="/admin/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/finanzas"
        element={
          <ProtectedRoute>
            <AdminFinanzasPage />
          </ProtectedRoute>
        }
      />

      {/* Parent portal — login + dashboard (authenticated) */}
      <Route path="/parent/login" element={<ParentLogin />} />
      <Route path="/parent/forgot-password" element={<ForgotPassword />} />
      <Route
        path="/parent/dashboard"
        element={
          <ParentRoute>
            <ParentDashboard />
          </ParentRoute>
        }
      />

      {/* Public: view/pay by registration ID only (no login); student is created when payment completes */}
      <Route path="/pagos/:registrationId" element={<PagosByRegistrationId />} />
      <Route path="/pay/by-id/:registrationId" element={<PayByRegistrationId />} />
      {/* Azul return: backend redirects here after payment */}
      <Route path="/pay/azul/approved" element={<AzulPaymentResult />} />
      <Route path="/pay/azul/declined" element={<AzulPaymentResult />} />
      <Route path="/pay/azul/cancel" element={<AzulPaymentResult />} />
      <Route path="/pay/azul/error" element={<AzulPaymentResult />} />

      {/* Forum (Talent School registration) as main page; /inscripcion alias for production verification */}
      <Route path="/" element={<Forum />} />
      <Route path="/inscripcion" element={<Forum />} />

      {/* Legal / policy pages */}
      <Route path="/politica-privacidad" element={<PrivacyPolicy />} />
      <Route path="/politica-reembolsos" element={<RefundPolicy />} />
      <Route path="/terminos-uso" element={<TermsOfUse />} />

      {/* 404 - redirect to forum home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

// =============================================================================
// APP COMPONENT
// =============================================================================

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          {RECAPTCHA_SITE_KEY ? (
            <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
              <AppRoutes />
            </GoogleReCaptchaProvider>
          ) : (
            <AppRoutes />
          )}
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
